import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { usePayerSharedFileRequirementConfig } from "./configs/payer/usePayerSharedFileRequirementConfig";
import { usePayeeSharedFileRequirementConfig } from "./configs/payee/usePayeeSharedFileRequirementConfig";
import { useMemo } from "react";
import { useWSQueries } from "@ws-react-query";
import { QUERY_SHARED_FILE } from "../../../../query/sharedFile/keys";
import { getSharedFile } from "../../../../services/sharedFile/getSharedFile";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";
import { getSharedFileFromCache } from "./configs/helpers/getSharedFileFromCache";
import { ISharedFileRequestResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.SharedFile &&
        requirement.dataSourceId
    )
    .map(requirement => ({
      queryKey: [QUERY_SHARED_FILE, requirement.dataSourceId],
      queryFn: () => getSharedFile(requirement.dataSourceId),
      ...sharedRequirementsQueriesConfig
    }));

  return useWSQueries({ queries });
};

export const useSharedFileRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.SharedFile] => {
  const contextQueries = useContextQueries(requirements);

  const payerSharedFileRequirementConfig =
    usePayerSharedFileRequirementConfig(callback);
  const payeeSharedFileRequirementConfig =
    usePayeeSharedFileRequirementConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<ISharedFileRequestResponse, RequirementType.SharedFile>({
      payerConfig: payerSharedFileRequirementConfig,
      payeeConfig: payeeSharedFileRequirementConfig,
      getAdditionalData: getSharedFileFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getSharedFileFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
